import React, { useRef, useState } from "react"
import { Box, CircularProgress, Stack, Typography } from "@mui/material"

interface WizardDropZoneProps {
  accept: string
  title: string
  subtitle: string
  loading: boolean
  onFileSelected: (file: File) => void
  icon: React.ReactNode
}

const WizardDropZone: React.FC<WizardDropZoneProps> = ({ accept, title, subtitle, loading, onFileSelected, icon }) => {
  const [dragActive, setDragActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDrag = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDragActive(event.type === "dragenter" || event.type === "dragover")
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setDragActive(false)

    const [file] = Array.from(event.dataTransfer.files)
    if (file) onFileSelected(file)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) onFileSelected(file)
  }

  return (
    <Box>
      <input type="file" ref={inputRef} onChange={handleChange} accept={accept} hidden />
      <Box
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: "2px dashed",
          borderColor: dragActive ? "primary.main" : "divider",
          borderRadius: 4,
          p: { xs: 4, sm: 6 },
          textAlign: "center",
          cursor: "pointer",
          bgcolor: dragActive
            ? "action.hover"
            : theme => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.015)" : "rgba(0,0,0,0.01)"),
          transition: "all 0.2s ease-in-out",
          "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" }
        }}>
        {loading ? (
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Processing and validating uploaded file contents...
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}>
              {icon}
            </Box>
            <Stack spacing={0.5}>
              <Typography variant="h6" fontWeight={700}>
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Box>
    </Box>
  )
}

export default WizardDropZone
